import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import PaywallSheet, { type QuotaKind } from "@/components/paywall/PaywallSheet";
import type { PlanTier } from "@/lib/quotaError";
import { analytics } from "@/lib/analytics";

// V4.3 — single app-level PaywallSheet, opened from anywhere (streaming
// surfaces, invoke hooks, module taps). The monthly quota cap is the main
// Free→Premium conversion moment, so it gets the full sheet (not a toast).
//
// No session dedup by design: re-opening the sheet on each blocked send is
// the intended behavior (every blocked action is a fresh conversion prompt).

// `placement` attributes WHERE the paywall fired (e.g. "daily_limit") for the
// paywall_shown / paywall_cta_click / paywall_dismissed funnel — orthogonal to
// `variant` (WHAT is being sold). Falls back to the variant name when absent.
// `quotaScope` picks daily vs monthly copy inside the quota_exceeded variant.
export type PaywallRequest = (
  | { variant: "module_locked"; moduleId?: string }
  | {
    variant: "quota_exceeded";
    quotaKind?: QuotaKind;
    quotaScope?: "daily" | "monthly";
    /** ISO do fim do período (o 402 traz period_end) — copy de renovação. */
    periodEnd?: string;
    /** Tier de quem bateu o teto — Premium sem minutos de LIVE não vê upsell de Premium. */
    currentTier?: PlanTier;
  }
  | { variant: "voice_cap" }
  | { variant: "generic" }
) & { placement?: string };

interface PaywallContextValue {
  openPaywall: (req: PaywallRequest) => void;
  closePaywall: () => void;
}

const PaywallContext = createContext<PaywallContextValue>({
  openPaywall: () => {},
  closePaywall: () => {},
});

export const PaywallProvider = ({ children }: { children: React.ReactNode }) => {
  const [request, setRequest] = useState<PaywallRequest | null>(null);

  const openPaywall = useCallback((req: PaywallRequest) => {
    // Source = the paywall variant (module_locked / quota_exceeded / voice_cap
    // / generic) — every blocked action is a fresh conversion prompt.
    analytics.paywallViewed(req.variant);
    // Placement-attributed funnel (paywall_shown → cta_click | dismissed).
    // Kept SEPARATE from paywall_viewed so the historic metric stays clean.
    analytics.paywallShown(req.placement ?? req.variant, req.variant);
    setRequest(req);
  }, []);
  const closePaywall = useCallback(() => setRequest(null), []);

  const value = useMemo(() => ({ openPaywall, closePaywall }), [openPaywall, closePaywall]);

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PaywallSheet
        open={request !== null}
        onClose={closePaywall}
        variant={request?.variant ?? "generic"}
        moduleId={request && "moduleId" in request ? request.moduleId : undefined}
        quotaKind={request && "quotaKind" in request ? request.quotaKind : undefined}
        quotaScope={request && "quotaScope" in request ? request.quotaScope : undefined}
        periodEnd={request && "periodEnd" in request ? request.periodEnd : undefined}
        currentTier={request && "currentTier" in request ? request.currentTier : undefined}
        placement={request?.placement}
      />
    </PaywallContext.Provider>
  );
};

export const usePaywall = () => useContext(PaywallContext);
